import * as Yup from 'yup';

import { baseGraphSetupValidationSchema } from 'src/app/graph/shared/graphSetupHelpers';

/**
 * Types inferred from `validationSchema`
 */
export type FormValues = Yup.InferType<typeof graphSetupValidationSchema>;

function getDefaultSchema(partialSchema: string) {
  return `# These are the necessary federation and connector directives you will be using
extend schema
  @link(url: "https://specs.apollo.dev/federation/v2.10")
  @link(
    url: "https://specs.apollo.dev/connect/v0.1"
    import: ["@connect", "@source"]
  )
  # A @source directive defines a shared data source for multiple connectors.
  @source(
    name: "jsonPlaceholder"
    http: { baseURL: "https://jsonplaceholder.typicode.com/" }
  )
${partialSchema}
`;
}

const USERS_DEFAULT_SCHEMA = getDefaultSchema(`
type User {
  id: ID!
  name: String
  username: String
}

type Query {
  users: [User]
    # A @connect directive defines the API data source of a GraphQL schema field.
    @connect(
      source: "jsonPlaceholder"
      http: { GET: "/users" }
      selection: """
      id
      name
      username
      """
    )
}
`);

const POSTS_DEFAULT_SCHEMA = getDefaultSchema(`
type Post {
  id: ID!
  body: String
  title: String
}

type Query {
  posts: [Post]
    # A @connect directive defines the API data source of a GraphQL schema field.
    @connect(
      source: "jsonPlaceholder"
      http: { GET: "/posts" }
      selection: """
      id
      title
      body
      """
    )
}
`);

const TODOS_DEFAULT_SCHEMA = getDefaultSchema(`
type Todo {
  id: ID!
  title: String
  completed: Boolean
}

type Query {
  todos: [Todo]
    # A @connect directive defines the API data source of a GraphQL schema field.
    @connect(
      source: "jsonPlaceholder"
      http: { GET: "/todos" }
      selection: """
      id
      title
      completed
      """
    )
}
`);

export const PRODUCTS_DEFAULT_SCHEMA = `
# These are the necessary federation and connector directives you will be using
extend schema
  @link(
    url: "https://specs.apollo.dev/federation/v2.10"
    import: ["@key"]
  )
  @link(
    url: "https://specs.apollo.dev/connect/v0.1"
    import: ["@connect", "@source"]
  )
# A @source directive defines a shared data source for multiple connectors.
  @source(
    name: "ecomm"
    http: { baseURL: "https://ecommerce.demo-api.apollo.dev/" }
  )

type Product {
  id: ID!
  name: String
  description: String
}

type Query {
  products: [Product]
    # A @connect directive defines the API data source of a GraphQL schema field.
    @connect(
      source: "ecomm"
      http: { GET: "/products" }
      selection: """
      $.products {
        id
        name
        description
      }
      """
    )
}`;

export type DefaultSDLKey = 'users' | 'posts' | 'todos';

export const DEFAULT_SDLS: Record<DefaultSDLKey, string> = {
  users: USERS_DEFAULT_SCHEMA,
  posts: POSTS_DEFAULT_SCHEMA,
  todos: TODOS_DEFAULT_SCHEMA,
};

export const DEFAULT_VARIANT_NAME = 'main';

const sdls = [...Object.keys(DEFAULT_SDLS), 'products'];

/**
 * Schema for the form
 */
export const graphSetupValidationSchema = baseGraphSetupValidationSchema.concat(
  Yup.object().shape({
    subgraphName: Yup.mixed()
      .oneOf(sdls)
      .required('Please select a subgraph name.'),
    hiddenFromUninvitedNonAdminAccountMembers: Yup.boolean().required(),
  }),
);
